import {
  ActionError,
  AttentionBlock,
  Button,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadOnlyField,
  ReadingColumn,
  SavedIndicator,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useClinicProfile, useSaveClinicProfile, useSignatureProvider } from './api'
import {
  AddressBlock,
  AutoSignatureDialog,
  ClinicSignatureBlock,
  ContactBlock,
  FormSkeleton,
  DocumentField,
  IdentificationBlock,
  LegalRepresentativeBlock,
  WitnessesBlock,
} from './blocks'
import {
  ADDRESS_KEYS,
  CONTACT_KEYS,
  EMPTY_CLINIC_FORM,
  IDENTIFICATION_KEYS,
  REPRESENTATIVE_KEYS,
  WITNESS_KEYS,
  clinicFieldKey,
  clinicFormFrom,
  companyInput,
  documentInput,
  DOC_NUMBER_KEYS,
  placeFieldErrors,
  representativeInput,
  validateClinicForm,
  witnessesInput,
  type ClinicFormState,
} from './form'
import { audiencesLabel } from './labels'
import { LOGO_FIELD_KEYS, logoFormFrom, logoInput, type LogoFormState } from './logo'
import { LogoBlock } from './logo-block'
import { focusFirstInvalid, useFormState } from './use-form-state'
import { useLogoField } from './use-logo-field'

/**
 * Empresa: o cadastro da clínica ativa (docs/areas/administracao.md, "Empresa"; RN-ADM-01 a 09).
 * Arquétipo: cadastro. Gestora e root com clínica ativa editam; recepção e prestador leem a mesma
 * tela sem Salvar. O que muda aqui vale para os próximos documentos (RN-ADM-02). O logo é enviado
 * ao ser escolhido e confirmado no Salvar, junto do resto (RN-ADM-05).
 */

/** O cadastro mais o logo, que só Empresa edita. */
type ProfileFormState = ClinicFormState & LogoFormState

const EMPTY_FORM: ProfileFormState = {
  ...EMPTY_CLINIC_FORM,
  ...logoFormFrom({ logoPosition: 'header' }),
}

const fieldKey = (path: string, values: ProfileFormState): keyof ProfileFormState | undefined =>
  LOGO_FIELD_KEYS[path] ?? clinicFieldKey(path, values)

// O documento é da Westhetics (RN-ADM-01) e os públicos também (RN-ADM-57): não entram na conferência.
const KEYS = [
  ...IDENTIFICATION_KEYS,
  ...ADDRESS_KEYS,
  ...CONTACT_KEYS,
  ...REPRESENTATIVE_KEYS,
  ...WITNESS_KEYS,
]

export function ClinicProfilePage() {
  const query = useClinicProfile()
  // O formulário nasce do cadastro carregado; outra clínica, outro formulário.
  return <ClinicProfileForm key={query.data?.id ?? 'loading'} query={query} />
}

function ClinicProfileForm({ query }: { query: ReturnType<typeof useClinicProfile> }) {
  const session = useSession()
  const canEdit = session.canManageClinic
  const profile = query.data
  const save = useSaveClinicProfile()
  const form = useFormState<ProfileFormState>(
    () => (profile ? { ...clinicFormFrom(profile), ...logoFormFrom(profile) } : EMPTY_FORM),
    validateClinicForm,
  )
  const logo = useLogoField(form, profile?.logo ?? null)
  const [confirmingAutomatic, setConfirmingAutomatic] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  // A assinatura é sempre pela Autentique, configurada pela Westhetics (T49): a clínica não escolhe
  // o provedor. O bloco da assinatura da clínica só vale para o fluxo próprio (provedor `westhetics`).
  const provider = useSignatureProvider().data?.provider
  const savedToast = 'Dados da empresa salvos — passam a valer nos próximos documentos'

  function send() {
    const values = form.values
    save.mutate(
      {
        ...(profile?.isDocNumberEditable ? documentInput(values) : {}),
        ...companyInput(values),
        ...representativeInput(values),
        ...logoInput(values),
        ...witnessesInput(values),
      },
      {
        onSuccess: () => {
          setConfirmingAutomatic(false)
          // O que foi enviado virou o logo da clínica: não há mais troca a confirmar.
          logo.settle()
          form.markSaved()
          toast(savedToast)
        },
        onError: (error) => {
          setConfirmingAutomatic(false)
          form.refuse(placeFieldErrors(error, (path) => fieldKey(path, values)).fields)
          focusFirstInvalid(formRef.current)
        },
      },
    )
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    // Enquanto o arquivo sobe, o `logoId` ainda não existe: salvar agora deixaria o logo de fora.
    if (!canEdit || !profile || logo.isUploading) return
    if (!form.check(profile.isDocNumberEditable ? [...DOC_NUMBER_KEYS, ...KEYS] : KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    // Ativar a assinatura automática pede confirmação antes de salvar (RN-ADM-07).
    const isActivating =
      form.values.clinicSignatureMode === 'automatic' && profile.clinicSignatureMode === 'manual'
    if (isActivating) setConfirmingAutomatic(true)
    else send()
  }

  const failure = save.isError
    ? placeFieldErrors(save.error, (path) => fieldKey(path, form.values))
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader crumbs={[{ label: 'Administração' }, { label: 'Empresa' }]} />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Empresa"
            subtitle="Estes dados entram nos documentos gerados: razão social, endereço, testemunhas e logo. Errar aqui contamina todo contrato emitido."
          />

          {query.isError ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos abrir os dados da empresa"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : !profile ? (
            <FormSkeleton blocks={5} />
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {!canEdit ? (
                <AttentionBlock variant="lilac">
                  Só a gestora altera estes dados. Você os vê como entram nos documentos.
                </AttentionBlock>
              ) : null}

              {showFailure ? (
                <ActionError
                  title="Não foi possível salvar os dados da empresa"
                  description={
                    failure.unplaced.length > 0
                      ? failure.unplaced.join(' ')
                      : `${errorMessage(save.error)} O que você digitou continua na tela.`
                  }
                />
              ) : null}

              <LogoBlock {...logo.block} readOnly={!canEdit} />
              <IdentificationBlock
                form={form}
                readOnly={!canEdit}
                note="Aparece no cabeçalho e na qualificação das partes."
                document={<DocumentField form={form} profile={profile} readOnly={!canEdit} />}
              >
                <ReadOnlyField
                  label="Públicos"
                  note="· definidos pela Westhetics"
                  value={audiencesLabel(profile.audiences)}
                />
              </IdentificationBlock>
              <AddressBlock form={form} readOnly={!canEdit} />
              <ContactBlock form={form} readOnly={!canEdit} />
              <LegalRepresentativeBlock form={form} readOnly={!canEdit} />
              <WitnessesBlock form={form} readOnly={!canEdit} />
              {/* A assinatura da clínica nas telas da Westhetics; na Autentique, ela assina lá (RN-ASS-37). */}
              {provider === 'westhetics' ? (
                <ClinicSignatureBlock
                  form={form}
                  readOnly={!canEdit}
                  autoSignature={profile.autoSignature}
                />
              ) : null}
              {canEdit ? (
                <FormFooter
                  status={
                    <SavedIndicator
                      savedAt={new Date(profile.updatedAt)}
                      dirty={form.dirty}
                      saving={save.isPending}
                    />
                  }
                  primary={
                    <Button
                      type="submit"
                      disabled={logo.isUploading}
                      loading={save.isPending}
                      loadingLabel="Salvando…"
                    >
                      {logo.isUploading ? 'Enviando o logo…' : 'Salvar'}
                    </Button>
                  }
                />
              ) : null}
            </div>
          )}
        </ReadingColumn>
      </Page>

      <AutoSignatureDialog
        open={confirmingAutomatic}
        onOpenChange={setConfirmingAutomatic}
        loading={save.isPending}
        onConfirm={send}
      />
    </form>
  )
}
